/**
 * VRG-736 — the TEXT of a deal's value formulas, with the deal's own numbers and rates, for the
 * tooltips on predicted ARR, activation share, tag margin and predicted annual value (Spencer,
 * 2026-10-22). Shared so the platform's tooltips and the API guard read the same words: a
 * tooltip that shows a formula is a claim about the arithmetic, and the guard
 * (`sales-predicted-value.guard.spec.ts`) checks the claim names every rate it used.
 *
 * Each says where its rates come from: the plan (and where the plan came from), the activation
 * schedule (synced from Stripe), the share and its default, and the tag cost and markup (marked
 * placeholders until the supply price list, VRG-143).
 */
import {DEAL_PLAN_LABELS, isDealPlan, DEAL_VALUE_BASIS_LABELS, activationShareWhy, percentLabel, TAG_COST_PLACEHOLDER_NOTE} from './sales-pipeline.js';
import type {ActivationShare, PredictedArrBreakdown, PotentialPlanSource, TagMargin} from './sales-pipeline.js';

const usd = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;
const usdCents = (n: number): string => `$${n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
const count = (n: number): string => Math.round(n).toLocaleString('en-US');

const PLAN_SOURCE_WORDS: Readonly<Record<PotentialPlanSource, string>> = {
  stripe: 'from Stripe',
  deal: 'set on the deal',
  form: 'the waitlist form',
  tier: "the tier's default plan",
};

const planName = (key: string | null): string => (key && isDealPlan(key) ? DEAL_PLAN_LABELS[key] : key ? key.charAt(0).toUpperCase() + key.slice(1) : 'No plan');

/** "12,000 units × 20% (Tier 2 default) = 2,400 activations". */
export const activationShareFormula = (units: number | null, share: ActivationShare, expected: number | null): string =>
  units === null || expected === null
    ? `Activation share ${percentLabel(share.share)} (${activationShareWhy(share)}). No production figure yet, so no activations to price.`
    : `${count(units)} units × ${percentLabel(share.share)} (${activationShareWhy(share)}) = ${count(expected)} activations a year. Defaults: Tier 1–2 20% (pilot penetration, investor model), Tier 3–5 100%.`;

/**
 * Predicted ARR, e.g. "Plan Business $299/mo × 12 = $3,588 (set on the deal) · 12,000 units × 20%
 * (Tier 2 default) = 2,400 activations → $34,560 on the graduated activation schedule from Stripe
 * · Predicted ARR $38,148. Subscription and activations only: tags are a separate line."
 */
export const predictedArrFormula = (b: PredictedArrBreakdown): string => {
  const parts: string[] = [];
  if (b.plan_annual !== null && b.plan_monthly !== null) {
    parts.push(
      `Plan ${planName(b.plan)} ${usd(b.plan_monthly)}/mo × 12 = ${usd(b.plan_annual)} (${b.plan_source ? PLAN_SOURCE_WORDS[b.plan_source] : 'plan'}${b.plan_substituted ? '; Enterprise priced as Scale, a floor' : ''})`,
    );
  } else if (b.plan_annual !== null) {
    parts.push(`Plan ${planName(b.plan)} ${usd(b.plan_annual)} a year`);
  } else {
    parts.push('No plan to price');
  }
  if (b.units !== null && b.expected_activations !== null) {
    parts.push(
      `${count(b.units)} units × ${percentLabel(b.share.share)} (${activationShareWhy(b.share)}) = ${count(b.expected_activations)} activations → ${
        b.activation_annual !== null ? usd(b.activation_annual) : 'not priced'
      } on the graduated activation schedule from Stripe`,
    );
  } else {
    parts.push('no production figure, so no activations');
  }
  const calc = b.calculated !== null ? usd(b.calculated) : 'nothing';
  if (b.basis === 'manual') {
    const shown = b.arr !== null ? `${b.currency === 'USD' ? usd(b.arr) : `${count(b.arr)} ${b.currency}`}` : '—';
    return `Manual figure ${shown}, set on the deal: a manual figure always wins for ARR. Calculated, it would be: ${parts.join(' · ')} = ${calc}.`;
  }
  const basis = b.basis ? ` (${DEAL_VALUE_BASIS_LABELS[b.basis]} basis)` : '';
  return `${parts.join(' · ')} · Predicted ARR ${b.arr !== null ? usd(b.arr) : '—'}${basis}. Subscription and activations only: tags are a separate line. A manual figure on the deal would override it.`;
};

/** "Tags 2,400 × $1.00 × 35% = $840 a year · tag cost placeholder until the supply price list (VRG-143)". */
export const tagMarginFormula = (t: TagMargin | null): string =>
  t === null
    ? 'No production figure yet, so no tagged units. Tag margin = expected activations × tag unit cost × markup.'
    : `Tags ${count(t.tagged_units)} × ${usdCents(t.unit_cost_usd)} × ${percentLabel(t.markup)} = ${usd(t.annual)} a year. Tagged units = expected activations. Tag cost ${
        t.unit_cost_placeholder ? TAG_COST_PLACEHOLDER_NOTE : 'from the supply price list'
      }; markup ${t.markup_source === 'placeholder' ? 'a placeholder (Sales → Tag economics)' : 'set in Sales → Tag economics'}. Hardware margin, so never in ARR.`;

/** "Predicted ARR $38,148 + tag margin $840 = $38,988 a year". */
export const predictedAnnualValueFormula = (arr: {amount: number | null; currency: string}, t: TagMargin | null, total: number | null): string =>
  total === null
    ? arr.amount !== null && arr.currency !== 'USD'
      ? `The predicted ARR is in ${arr.currency}; tag margin is USD and vera-2 has no FX rate, so there is no annual value.`
      : 'No predicted ARR yet, so no annual value.'
    : `Predicted ARR ${usd(arr.amount ?? 0)} + tag margin ${t ? usd(t.annual) : '$0 (no production figure)'} = ${usd(total)} a year. ARR is the recurring part; tags are hardware.`;
